"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { formatHours, formatOereRange, toPriceView } from "@/features/pricing/shared/price-view";
import { usePriceBand } from "@/features/pricing/ui/usePriceBand";
import { formatKr } from "@/shared/format/money";

// The calculator shows the SAME band the match ranking prices companies
// against (via /api/pris-estimat). So only services the model can actually
// price: window cleaning is area-independent (price per window), so it's
// excluded.
type ServiceType = "fast-privat" | "engangs-flytte" | "engangs-hoved" | "erhverv-kontor";
type Frequency = "ugentlig" | "14-dag" | "maanedlig";

const SERVICE_OPTIONS: {
  value: ServiceType;
  label: string;
  description: string;
}[] = [
  {
    value: "fast-privat",
    label: "Fast privat rengøring",
    description: "Tilbagevendende rengøring i dit hjem — ugentligt eller 14-dagligt",
  },
  {
    value: "engangs-flytte",
    label: "Flytterengøring",
    description: "Engangsopgave ved fraflytning — pris efter areal",
  },
  {
    value: "engangs-hoved",
    label: "Hovedrengøring",
    description: "Den mest grundige rengøring — alt fra top til bund",
  },
  {
    value: "erhverv-kontor",
    label: "Erhvervsrengøring (kontor)",
    description: "Fast rengøring af kontor og erhvervslokaler",
  },
];

const FREQUENCY_OPTIONS_PRIVAT: { value: Frequency; label: string }[] = [
  { value: "ugentlig", label: "Ugentlig" },
  { value: "14-dag", label: "Hver 14. dag" },
  { value: "maanedlig", label: "Månedlig" },
];

const FREQUENCY_OPTIONS_ERHVERV: { value: Frequency; label: string }[] = [
  { value: "ugentlig", label: "Daglig / ugentlig" },
  { value: "14-dag", label: "Hver 14. dag" },
  { value: "maanedlig", label: "Månedlig" },
];

const PROPERTY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "lejlighed", label: "Lejlighed" },
  { value: "hus", label: "Hus" },
  { value: "raekkehus", label: "Rækkehus" },
  { value: "sommerhus", label: "Sommerhus" },
];

// Maps the calculator's frequency values to the wizard's/price model's. Same
// mapping is used for the CTA link, so the number the customer sees here
// carries through into the wizard.
const FREQUENCY_TO_MODEL: Record<Frequency, string> = {
  ugentlig: "ugentligt",
  "14-dag": "hver-14-dag",
  maanedlig: "maanedligt",
};

const ENGANGS_SUBTYPE: Partial<Record<ServiceType, string>> = {
  "engangs-flytte": "flytterengoering",
  "engangs-hoved": "hovedrengoering",
};

const fieldClass =
  "w-full rounded border border-border bg-white px-3.5 py-3 text-[15px] text-ink placeholder:text-ink-subtle focus:border-theme-accent focus:outline-none focus:ring-2 focus:ring-theme-accent/20";
const labelClass = "mb-1.5 block text-[13px] font-medium text-ink";

export function PriceCalculator() {
  const [service, setService] = useState<ServiceType | null>(null);
  const [sqm, setSqm] = useState(80);
  const [postalCode, setPostalCode] = useState("");
  const [frequency, setFrequency] = useState<Frequency | null>(null);
  const [propertyType, setPropertyType] = useState("");

  const requiresFrequency = service === "fast-privat" || service === "erhverv-kontor";
  const isBusiness = service === "erhverv-kontor";
  const recurring = service === "fast-privat" || service === "erhverv-kontor";

  const query = useMemo(() => {
    if (!service) return null;
    if (requiresFrequency && !frequency) return null;

    const params = new URLSearchParams({
      clientType: isBusiness ? "erhverv" : "privat",
      cleaningType: isBusiness ? "kontor" : recurring ? "fast-aftale" : "engangs",
      squareMeters: String(sqm),
    });
    const subtype = ENGANGS_SUBTYPE[service];
    if (subtype) params.set("engangsSubtype", subtype);
    if (frequency) params.set("frequency", FREQUENCY_TO_MODEL[frequency]);
    if (!isBusiness && propertyType) params.set("propertyType", propertyType);
    if (postalCode.length === 4) params.set("postalCode", postalCode);
    return params.toString();
  }, [service, requiresFrequency, isBusiness, recurring, sqm, frequency, propertyType, postalCode]);

  // Sliders update the query continuously — debounced so we don't fire a
  // request per pixel. The band is cached per query, so dragging back is free.
  const { band, loading } = usePriceBand(query, { debounceMs: 300 });

  const result = useMemo(
    () =>
      band && service
        ? toPriceView(band, {
            clientType: isBusiness ? "erhverv" : "privat",
            recurring,
            frequency: frequency ? FREQUENCY_TO_MODEL[frequency] : null,
            engangsSubtype: ENGANGS_SUBTYPE[service] ?? null,
          })
        : null,
    [band, service, isBusiness, recurring, frequency],
  );

  const showFrequency = requiresFrequency;
  const showPropertyType = service !== null && !isBusiness;
  const showPostnr = service !== null;

  const sqmRange = isBusiness
    ? { min: 50, max: 1000, step: 25 }
    : service === "engangs-hoved"
      ? { min: 40, max: 250, step: 5 }
      : { min: 30, max: 250, step: 5 };

  const frequencyOptions = isBusiness ? FREQUENCY_OPTIONS_ERHVERV : FREQUENCY_OPTIONS_PRIVAT;

  const tilbudUrl =
    service && (!requiresFrequency || frequency)
      ? buildTilbudUrl({ service, sqm, postalCode, frequency: frequency ?? "14-dag" })
      : null;

  const primaryLabel = result ? (result.primaryUnit === "month" ? "pr. måned" : "engangs") : "";

  return (
    <div className="rounded border border-border bg-white p-6 lg:p-8">
      <div>
        <h2 className="font-serif text-xl font-bold tracking-[-0.01em] text-ink md:text-2xl">
          Beregn din pris
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">
          Vælg ydelse og udfyld dine oplysninger. Estimatet opdateres automatisk.
        </p>
      </div>

      <div className="mt-7 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="flex flex-col gap-6">
          <div>
            <span className={labelClass}>Hvilken service?</span>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {SERVICE_OPTIONS.map((opt) => {
                const active = service === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setService(opt.value)}
                    className={`cursor-pointer rounded border-2 p-4 text-left transition-colors ${
                      active
                        ? "border-theme-accent bg-[#F9FBF4] text-ink"
                        : "border-border bg-white text-ink-muted hover:border-ink"
                    }`}
                  >
                    <div className="text-[15px] font-medium text-ink">{opt.label}</div>
                    <div className="mt-0.5 text-xs leading-snug text-ink-subtle">
                      {opt.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {service !== null && (
            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className={labelClass}>
                  {isBusiness ? "Lokalets areal" : "Boligstørrelse"}
                </span>
                <span className="font-mono text-sm font-medium text-ink">{sqm} m²</span>
              </div>
              <input
                type="range"
                min={sqmRange.min}
                max={sqmRange.max}
                step={sqmRange.step}
                value={sqm}
                onChange={(e) => setSqm(Number(e.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-border accent-theme-accent"
                aria-label="Areal i kvadratmeter"
              />
              <div className="mt-1 flex justify-between font-mono text-[11px] text-ink-subtle">
                <span>{sqmRange.min} m²</span>
                <span>{sqmRange.max} m²</span>
              </div>
            </div>
          )}

          {showFrequency && (
            <div>
              <span className={labelClass}>Hvor ofte?</span>
              <div className="grid grid-cols-3 gap-2.5">
                {frequencyOptions.map((opt) => {
                  const active = frequency === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFrequency(opt.value)}
                      className={`cursor-pointer rounded border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                        active
                          ? "border-theme-accent bg-[#F9FBF4] text-ink"
                          : "border-border bg-white text-ink-muted hover:border-ink"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Same factor the price model uses (stairs, bathrooms, access).
              Optional: without a choice we compute neutrally. */}
          {showPropertyType && (
            <div>
              <span className={labelClass}>
                Boligtype{" "}
                <span className="font-normal text-ink-subtle">
                  (valgfri — justerer tidsforbrug)
                </span>
              </span>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {PROPERTY_TYPE_OPTIONS.map((opt) => {
                  const active = propertyType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPropertyType(active ? "" : opt.value)}
                      className={`cursor-pointer rounded border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                        active
                          ? "border-theme-accent bg-[#F9FBF4] text-ink"
                          : "border-border bg-white text-ink-muted hover:border-ink"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {showPostnr && (
            <div>
              <label htmlFor="pris-postnr" className={labelClass}>
                Postnummer{" "}
                <span className="font-normal text-ink-subtle">(valgfri — justerer for region)</span>
              </label>
              <input
                id="pris-postnr"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                placeholder="fx 2100"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className={`${fieldClass} max-w-[14rem]`}
              />
              {band?.region && (
                <p className="mt-1.5 text-xs text-ink-subtle">
                  Region: <span className="text-ink">{band.region}</span> — beregnet på{" "}
                  {band.sampleSize} firmaers timepriser
                </p>
              )}
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div
            className={`rounded border-2 p-6 ${
              result ? "border-theme-accent bg-paper" : "border-border bg-surface-muted"
            }`}
          >
            {result ? (
              <>
                <p className="font-mono text-[11px] uppercase tracking-wider text-ink-subtle">
                  Estimeret pris {result.exclVat ? "(ekskl. moms)" : "(inkl. moms)"}
                </p>
                <p className="mt-2 font-serif text-[2rem] leading-tight font-bold tracking-[-0.02em] text-ink">
                  {formatOereRange(
                    result.primaryUnit === "month" ? result.perMonth : result.perVisit,
                  )}
                </p>
                <p className="mt-0.5 text-sm text-ink-subtle">{primaryLabel}</p>

                <dl className="mt-5 space-y-2 border-t border-border pt-4 text-sm">
                  {result.primaryUnit === "month" && (
                    <DataRow label="Pr. besøg" value={formatOereRange(result.perVisit)} />
                  )}
                  <DataRow label="Timepris" value={formatKr(result.perTimeOere)} />
                  <DataRow label="Anslået tid" value={formatHours(result.estimatedHours)} />
                </dl>

                {result.servicefradragYearlyOere && (
                  <div className="mt-4 rounded border border-border bg-white p-3 text-sm">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-ink-subtle">
                      Med servicefradrag
                    </p>
                    <p className="mt-1 leading-relaxed text-ink-muted">
                      Skattebesparelse:{" "}
                      <span className="font-medium text-ink">
                        {formatOereRange(result.servicefradragYearlyOere)}{" "}
                        {/* A recurring agreement saves this every year. A one-off saves
                            it once, so "pr. år" would overstate it. */}
                        {result.primaryUnit === "month" ? "pr. år" : "på denne opgave"}
                      </span>
                    </p>
                  </div>
                )}

                {tilbudUrl && (
                  <Link
                    href={tilbudUrl}
                    className="mt-5 block rounded bg-theme py-3 text-center text-[15px] font-medium text-white hover:bg-theme-dark"
                  >
                    Få et konkret tilbud →
                  </Link>
                )}
                <p className="mt-2.5 text-center text-xs text-ink-subtle">
                  <Link href="/metodologi" className="text-link underline underline-offset-2">
                    Sådan beregner vi
                  </Link>
                </p>

                {result.notes.length > 0 && (
                  <div className="mt-4 border-t border-border pt-3 text-xs leading-relaxed text-ink-subtle">
                    {result.notes.map((note) => (
                      <p key={note} className="mt-1">
                        {note}
                      </p>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="font-mono text-[11px] uppercase tracking-wider text-ink-subtle">
                  Estimat
                </p>
                <p className="mt-2 font-serif text-[1.5rem] leading-tight font-bold text-ink">
                  {service === null
                    ? "Vælg ydelse"
                    : requiresFrequency && !frequency
                      ? "Vælg frekvens"
                      : loading
                        ? "Beregner …"
                        : "Ingen estimat"}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                  {service === null
                    ? "Beregneren viser et prisestimat, så snart du har valgt hvilken type rengøring du har brug for."
                    : requiresFrequency && !frequency
                      ? "For løbende rengøring skal vi vide, hvor ofte rengøringen skal udføres."
                      : loading
                        ? "Vi henter, hvad firmaerne typisk byder på en opgave som denne."
                        : "Vi kunne ikke beregne et estimat for den kombination. Bed om et konkret tilbud i stedet."}
                </p>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-ink-subtle">{label}</dt>
      <dd className="font-medium text-ink">{value}</dd>
    </div>
  );
}

function buildTilbudUrl(input: {
  service: ServiceType;
  sqm: number;
  postalCode: string;
  frequency: Frequency;
}): string {
  const params = new URLSearchParams();
  if (input.postalCode.length === 4) params.set("postalCode", input.postalCode);

  switch (input.service) {
    case "fast-privat":
      params.set("type", "privat");
      params.set("task", "lobende");
      params.set("frequency", FREQUENCY_TO_MODEL[input.frequency]);
      params.set("kvm", String(input.sqm));
      break;
    case "engangs-flytte":
      params.set("type", "privat");
      params.set("task", "engangs");
      params.set("subtype", "flytterengoering");
      params.set("kvm", String(input.sqm));
      break;
    case "engangs-hoved":
      params.set("type", "privat");
      params.set("task", "engangs");
      params.set("subtype", "hovedrengoering");
      params.set("kvm", String(input.sqm));
      break;
    case "erhverv-kontor":
      params.set("type", "erhverv");
      params.set("task", "lobende");
      params.set("business", "kontor");
      params.set("frequency", FREQUENCY_TO_MODEL[input.frequency]);
      params.set("kvm", String(input.sqm));
      break;
  }

  const qs = params.toString();
  return qs ? `/tilbud?${qs}` : "/tilbud";
}
